// SPDX-License-Identifier: AGPL-3.0-or-later

import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import {initializeStore} from '@app/features/platform/utils/StoreInitialization';
import Window from '@app/features/window/state/Window';
import {makeAutoObservable, observableShallow} from 'mobx';

export class GameModeState {
	enabled = true;
	// Micup: lite mode keeps the app light with its window in front too (per device, off by default, apart from the
	// unfocused game mode above). Sound, screen share and notifications are not touched; the user's own settings stay as
	// they are and come back when it ends.
	lightMode = false;
	// Lite mode draws a 3D room as the flat call view.
	flatRoom = false;
	// Lite mode left on from the last session is asked about once per launch, until the user keeps it on (per device).
	// Turning lite mode off clears it.
	keepOnAcknowledged = false;
	launchReminderDue = false;
	private focusedPopouts = new Set<object>();
	// Others' cameras opened with a click while lite mode is on; for this session only.
	private openedCameras = new Set<string>();

	constructor() {
		makeAutoObservable<this, 'focusedPopouts' | 'openedCameras'>(
			this,
			{focusedPopouts: observableShallow, openedCameras: observableShallow},
			{autoBind: true},
		);
		void initializeStore(this, () =>
			makePersistent(this, 'GameMode', ['enabled', 'lightMode', 'flatRoom', 'keepOnAcknowledged']),
		).then(this.settleLaunchReminder);
	}

	get appFocused(): boolean {
		return Window.focused || this.focusedPopouts.size > 0;
	}

	get active(): boolean {
		return this.enabled && !this.appFocused;
	}

	get suspendsHiddenVideo(): boolean {
		return this.enabled && !Window.visible;
	}

	get shouldFlattenRoom(): boolean {
		return this.lightMode && this.flatRoom;
	}

	holdsCamera(identity: string): boolean {
		return this.lightMode && !this.openedCameras.has(identity);
	}

	setEnabled(enabled: boolean): void {
		this.enabled = enabled;
	}

	setLightMode(lightMode: boolean): void {
		this.lightMode = lightMode;
		if (lightMode) return;
		this.openedCameras.clear();
		this.keepOnAcknowledged = false;
		this.launchReminderDue = false;
	}

	keepLightModeOn(): void {
		this.keepOnAcknowledged = true;
		this.launchReminderDue = false;
	}

	markLaunchReminderShown(): void {
		this.launchReminderDue = false;
	}

	private settleLaunchReminder(): void {
		this.launchReminderDue = this.lightMode && !this.keepOnAcknowledged;
	}

	setFlatRoom(flatRoom: boolean): void {
		this.flatRoom = flatRoom;
	}

	toggleLightMode(): void {
		this.setLightMode(!this.lightMode);
	}

	openCamera(identity: string): void {
		this.openedCameras.add(identity);
	}

	setPopoutFocused(popout: object, focused: boolean): void {
		if (focused) this.focusedPopouts.add(popout);
		else this.focusedPopouts.delete(popout);
	}
}

export default new GameModeState();
